import { CdkTrapFocus } from '@angular/cdk/a11y';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

export type DialogType = 'modal' | 'drawer';
export type DialogPresetSize = 'sm' | 'md' | 'lg';
export type DialogSize = DialogPresetSize | 'custom' | string;
export type DialogHeadingAlign = 'center' | 'start' | 'left';

const CLOSE_ANIMATION_MS = 200;

let nextDialogId = 0;
let scrollLockCount = 0;
let restorePageScroll: (() => void) | null = null;

/**
 * Casca de diálogo (modal centralizado ou drawer lateral) com backdrop padronizado.
 *
 * Componente apresentacional: o pai controla a visibilidade (`@if`) e remove o diálogo ao
 * receber `closeRequest`, que é emitido **após** a animação de saída.
 *
 * Entradas:
 * - `type`: `'modal'` (padrão) ou `'drawer'`.
 * - `size`: `'sm'` | `'md'` | `'lg'`. Modal: 400 / 520 / 720px. Drawer: 400 / 480 / 640px.
 *   Uma medida CSS (`'600px'`, `'min(760px, 100%)'`) vira largura customizada.
 * - `customWidth`: largura explícita. Número vira px. Tem prioridade sobre `size`.
 * - `height` / `customHeight`: altura do painel. Número vira px. `customHeight` ganha se os dois vierem.
 * - `heading`: título. Vazio omite o `<h2>` e o diálogo usa `ariaLabel`.
 * - `headingAlign`: `'center'` (padrão), `'start'` ou `'left'` (sinônimo de `start`).
 * - `headingSize`: tamanho da fonte do título. Número vira px.
 * - `headingColor`: cor do título (qualquer valor CSS, inclusive `var(--...)`).
 * - `eyebrow`: rótulo pequeno acima do título. Só aparece no drawer.
 * - `showBack`: mostra a seta de voltar e emite `backRequest`. Não fecha o diálogo.
 * - `closeOnBackdrop`: clique no fundo fecha. Padrão `true`.
 * - `disableClose`: bloqueia o X, o Esc e o clique no fundo.
 * - `restoreFocus`: devolve o foco a quem abriu. Padrão `true`.
 * - `ariaLabel`: nome acessível quando não há `heading`.
 *
 * Saídas:
 * - `closeRequest`: pedido de fechar, depois da animação de saída.
 * - `backRequest`: clique na seta de voltar, na hora.
 *
 * Slots de conteúdo:
 * - padrão: corpo (com scroll);
 * - `[dialog-header]`: conteúdo extra abaixo do título (ex.: badges de status);
 * - `[dialog-footer]`: rodapé de ações. Some sozinho quando não há conteúdo projetado.
 *
 * @example
 * ```html
 * @if (open()) {
 *   <app-dialog
 *     type="modal"
 *     size="600px"
 *     height="min(760px, 100%)"
 *     heading="Perfil de compra"
 *     headingAlign="start"
 *     headingSize="13px"
 *     headingColor="var(--primary-color-alt)"
 *     [showBack]="step() > 0"
 *     (backRequest)="previous()"
 *     (closeRequest)="open.set(false)"
 *   >
 *     <p>Conteúdo do passo.</p>
 *     <div dialog-footer>
 *       <button type="button" class="btn btn-primary" (click)="next()">Continuar</button>
 *     </div>
 *   </app-dialog>
 * }
 * ```
 *
 * Para mudar o foco inicial (por padrão, o botão de fechar) use `cdkFocusInitial` no conteúdo.
 */
@Component({
  selector: 'app-dialog',
  standalone: true,
  imports: [CdkTrapFocus, NgTemplateOutlet],
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogComponent {
  readonly type = input<DialogType>('modal');
  readonly size = input<DialogSize>('md');
  readonly customWidth = input<string | number | null>(null);
  readonly height = input<string | number | null>(null);
  readonly customHeight = input<string | number | null>(null);
  readonly heading = input('');
  readonly headingAlign = input<DialogHeadingAlign>('center');
  readonly headingSize = input<string | number | null>(null);
  readonly headingColor = input<string | null>(null);
  readonly eyebrow = input('');
  readonly ariaLabel = input('');
  readonly closeOnBackdrop = input(true);
  readonly disableClose = input(false);
  readonly restoreFocus = input(true);
  readonly showBack = input(false);

  readonly closeRequest = output<void>();
  readonly backRequest = output<void>();

  protected readonly titleId = `app-dialog-title-${nextDialogId++}`;
  protected readonly isDrawer = computed(() => this.type() === 'drawer');
  protected readonly closing = signal(false);

  protected readonly customInlineSize = computed<string | null>(() => {
    const explicitWidth = this.customWidth();
    if (explicitWidth !== null && explicitWidth !== undefined && explicitWidth !== '') {
      return typeof explicitWidth === 'number' ? `${explicitWidth}px` : explicitWidth;
    }

    const s = this.size();
    if (s && s !== 'sm' && s !== 'md' && s !== 'lg' && s !== 'custom') {
      return typeof s === 'number' ? `${s}px` : s;
    }

    return null;
  });

  protected readonly customBlockSize = computed<string | null>(() => {
    const explicitHeight = this.customHeight() ?? this.height();
    if (explicitHeight !== null && explicitHeight !== undefined && explicitHeight !== '') {
      return typeof explicitHeight === 'number' ? `${explicitHeight}px` : explicitHeight;
    }

    return null;
  });

  protected readonly customHeadingSize = computed<string | null>(() => {
    const size = this.headingSize();
    if (size !== null && size !== undefined && size !== '') {
      return typeof size === 'number' ? `${size}px` : size;
    }

    return null;
  });

  private readonly doc = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private readonly previouslyFocused = this.doc.activeElement;

  constructor() {
    this.lockPageScroll();
    this.destroyRef.onDestroy(() => {
      this.unlockPageScroll();
      this.restorePreviousFocus();
    });
  }

  protected onBackdropClick(): void {
    if (this.closeOnBackdrop()) {
      this.requestClose();
    }
  }

  protected onEscape(event: Event): void {
    event.stopPropagation();
    this.requestClose();
  }

  protected requestClose(): void {
    if (this.closing() || this.disableClose()) {
      return;
    }

    if (this.prefersReducedMotion()) {
      this.closeRequest.emit();
      return;
    }

    this.closing.set(true);
    const timer = setTimeout(() => {
      this.closeRequest.emit();
      this.closing.set(false);
    }, CLOSE_ANIMATION_MS);
    this.destroyRef.onDestroy(() => clearTimeout(timer));
  }

  private lockPageScroll(): void {
    if (scrollLockCount++ > 0) {
      return;
    }

    const win = this.doc.defaultView;
    const body = this.doc.body;
    const scrollY = win?.scrollY ?? 0;
    const { position, top, insetInline } = body.style;

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.insetInline = '0';

    restorePageScroll = () => {
      body.style.position = position;
      body.style.top = top;
      body.style.insetInline = insetInline;
      win?.scrollTo({ top: scrollY, behavior: 'instant' });
    };
  }

  private unlockPageScroll(): void {
    if (--scrollLockCount > 0) {
      return;
    }

    restorePageScroll?.();
    restorePageScroll = null;
  }

  private restorePreviousFocus(): void {
    if (this.restoreFocus() && this.previouslyFocused instanceof HTMLElement && this.previouslyFocused.isConnected) {
      this.previouslyFocused.focus({ preventScroll: true });
    }
  }

  private prefersReducedMotion(): boolean {
    return this.doc.defaultView?.matchMedia('(prefers-reduced-motion: reduce)').matches ?? false;
  }
}
